import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { callMCPTool, getGatewayHealth } from '../services/api/gateway';
import { findCustomerByCipp } from '../utils/findCustomerByCipp';
import { isPlatformIdSet } from '../utils/customerKey';

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Query key factory with hierarchical structure
 * Enables efficient cache invalidation at different granularities
 */
export const queryKeys = {
  gateway: {
    all: ['gateway'],
    health: ['gateway', 'health'],
  },
  tenants: {
    all: ['tenants'],
    list: (source) => ['tenants', 'list', source],
    detail: (source, id) => ['tenants', 'detail', source, id],
  },
  cipp: {
    all: ['cipp'],
    tenants: () => ['cipp', 'tenants'],
    tenant: (id) => ['cipp', 'tenant', id],
    users: (tenantId) => ['cipp', 'users', tenantId],
    licenses: (tenantId) => ['cipp', 'licenses', tenantId],
    securityDefaults: (tenantId) => ['cipp', 'securityDefaults', tenantId],
    secureScore: (tenantId) => ['cipp', 'secureScore', tenantId],
    upsellSummary: (tenantId) => ['cipp', 'upsellSummary', tenantId],
  },
  sophos: {
    all: ['sophos'],
    tenants: () => ['sophos', 'tenants'],
    endpoints: (tenantId) => ['sophos', 'endpoints', tenantId],
    health: (tenantId) => ['sophos', 'health', tenantId],
    alerts: (tenantId) => ['sophos', 'alerts', tenantId],
    firewalls: (tenantId) => ['sophos', 'firewalls', tenantId],
    licenses: (tenantId) => ['sophos', 'licenses', tenantId],
    mdrStatus: (tenantId) => ['sophos', 'mdrStatus', tenantId],
  },
  acronis: {
    all: ['acronis'],
    tenants: () => ['acronis', 'tenants'],
    resources: (tenantId) => ['acronis', 'resources', tenantId],
    health: (tenantId) => ['acronis', 'health', tenantId],
    activities: (tenantId) => ['acronis', 'activities', tenantId],
    agents: (tenantId) => ['acronis', 'agents', tenantId],
    policies: (tenantId) => ['acronis', 'policies', tenantId],
    usage: (tenantId) => ['acronis', 'usage', tenantId],
    m365: (tenantId) => ['acronis', 'm365', tenantId],
  },
  autotask: {
    all: ['autotask'],
    companies: () => ['autotask', 'companies'],
    tickets: (companyId) => ['autotask', 'tickets', companyId],
    stats: (companyId) => ['autotask', 'stats', companyId],
    sla: (companyId) => ['autotask', 'sla', companyId],
  },
  scalepad: {
    all: ['scalepad'],
    organizations: () => ['scalepad', 'organizations'],
    hardware: (orgId) => ['scalepad', 'hardware', orgId],
    warranty: (orgId) => ['scalepad', 'warranty', orgId],
    eol: (orgId) => ['scalepad', 'eol', orgId],
  },
  datto: {
    all: ['datto'],
    devices: (clientName) => ['datto', 'devices', clientName],
    deviceStatus: (deviceId) => ['datto', 'deviceStatus', deviceId],
    alerts: (clientName) => ['datto', 'alerts', clientName],
    backupJobs: (clientName) => ['datto', 'backupJobs', clientName],
    protectedAssets: (clientName, assetType) => ['datto', 'protectedAssets', clientName, assetType],
    saasDomains: (organizationId) => ['datto', 'saasDomains', organizationId],
    endpointAgents: (organizationId) => ['datto', 'endpointAgents', organizationId],
  },
  odin: {
    all: ['odin'],
    customers: () => ['odin', 'customers'],
    health: (customerId) => ['odin', 'health', customerId],
    reports: (customerId) => ['odin', 'reports', customerId],
  },
  knowledge: {
    all: ['knowledge'],
    sources: (department) => ['knowledge', 'sources', department],
    search: (query, department) => ['knowledge', 'search', query, department],
    stats: (department) => ['knowledge', 'stats', department],
    crawlStatus: (jobId) => ['knowledge', 'crawlStatus', jobId],
    sourceContent: (sourceId) => ['knowledge', 'sourceContent', sourceId],
  },
};

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Cache time constants for different data types
 * More granular control over cache behavior based on data volatility
 */
export const CACHE_TIMES = {
  // Static data - rarely changes
  STATIC: {
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000,    // 1 hour (formerly cacheTime)
  },
  // Semi-static data - changes occasionally
  SEMI_STATIC: {
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000,    // 30 minutes
  },
  // Dynamic data - changes frequently
  DYNAMIC: {
    staleTime: 2 * 60 * 1000,  // 2 minutes
    gcTime: 10 * 60 * 1000,    // 10 minutes
  },
  // Real-time data - always fresh
  REALTIME: {
    staleTime: 30 * 1000,      // 30 seconds
    gcTime: 5 * 60 * 1000,     // 5 minutes
  },
};

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Request deduplication map
 * Prevents duplicate in-flight requests for the same data
 */
const pendingRequests = new Map();

/**
 * Deduplicated MCP tool caller - ensures only one request per unique key
 */
const deduplicatedCallMCPTool = async (server, tool, args, requestKey) => {
  // Check if there's already a pending request
  if (pendingRequests.has(requestKey)) {
    return pendingRequests.get(requestKey);
  }

  // Create and store the promise
  const promise = callMCPTool(server, tool, args).finally(() => {
    pendingRequests.delete(requestKey);
  });

  pendingRequests.set(requestKey, promise);
  return promise;
};

/**
 * ⚡ Hook for checking gateway health with optimized polling
 */
export const useGatewayHealth = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.gateway.health,
    queryFn: getGatewayHealth,
    ...CACHE_TIMES.REALTIME,
    refetchInterval: 60000, // Refetch every minute
    refetchIntervalInBackground: false, // Don't poll when tab is hidden
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    ...options,
  });
};

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Enhanced generic hook for MCP tools
 * Features:
 * - Request deduplication
 * - Configurable cache strategies
 * - Background refetching
 * - Optimistic placeholder data
 *
 * @param {object} options Configuration options
 * @param {string} options.server - MCP server name
 * @param {string} options.tool - Tool name
 * @param {object} options.args - Tool arguments
 * @param {array} options.queryKey - Query key for caching
 * @param {string} options.cacheStrategy - 'STATIC' | 'SEMI_STATIC' | 'DYNAMIC' | 'REALTIME'
 * @param {any} options.placeholderData - Data to show while loading
 * @param {object} options.queryOptions - Additional React Query options
 */
export const useMCPQuery = ({
  server,
  tool,
  args = {},
  queryKey,
  enabled = true,
  cacheStrategy = 'SEMI_STATIC',
  placeholderData,
  select,
  ...queryOptions
}) => {
  // Generate unique request key for deduplication
  const requestKey = useMemo(
    () => JSON.stringify({ server, tool, args }),
    [server, tool, args]
  );

  // Memoize the query function to prevent recreating on each render
  const queryFn = useCallback(
    () => deduplicatedCallMCPTool(server, tool, args, requestKey),
    [server, tool, args, requestKey]
  );

  // Apply cache strategy
  const cacheConfig = CACHE_TIMES[cacheStrategy] || CACHE_TIMES.SEMI_STATIC;

  return useQuery({
    queryKey,
    queryFn,
    enabled,
    ...cacheConfig,
    placeholderData,
    select,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnMount: 'always', // Ensure fresh data on mount if stale
    refetchOnWindowFocus: false, // Prevent excessive refetches
    ...queryOptions,
  });
};

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Parallel queries for multiple MCP tools
 * Fetches multiple resources in parallel for faster dashboard loads
 */
export const useMCPQueries = (queries) => {
  const queryConfigs = useMemo(
    () =>
      queries.map(({ server, tool, args = {}, queryKey, enabled = true, cacheStrategy = 'SEMI_STATIC', ...options }) => {
        const requestKey = JSON.stringify({ server, tool, args });
        const cacheConfig = CACHE_TIMES[cacheStrategy] || CACHE_TIMES.SEMI_STATIC;

        return {
          queryKey,
          queryFn: () => deduplicatedCallMCPTool(server, tool, args, requestKey),
          enabled,
          ...cacheConfig,
          retry: 2,
          ...options,
        };
      }),
    [queries]
  );

  return useQueries({ queries: queryConfigs });
};

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Enhanced mutation hook with optimistic updates
 * @param {object} options Configuration options
 */
export const useMCPMutation = ({
  server,
  tool,
  onSuccess,
  onError,
  onMutate,
  invalidateKeys = [],
  optimisticUpdate,
  ...mutationOptions
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args) => callMCPTool(server, tool, args),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await Promise.all(
        invalidateKeys.map((key) => queryClient.cancelQueries({ queryKey: key }))
      );

      // Snapshot the previous values for rollback
      const previousData = {};
      if (optimisticUpdate) {
        invalidateKeys.forEach((key) => {
          previousData[JSON.stringify(key)] = queryClient.getQueryData(key);
        });

        // Apply optimistic update
        optimisticUpdate(queryClient, variables);
      }

      onMutate?.(variables);

      return { previousData };
    },
    onSuccess: (data, variables, context) => {
      // Invalidate related queries to refetch fresh data
      invalidateKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      console.error(`MCP mutation failed: ${server}/${tool}`, error);

      // Rollback optimistic updates on error
      if (context?.previousData) {
        Object.entries(context.previousData).forEach(([keyStr, data]) => {
          const key = JSON.parse(keyStr);
          queryClient.setQueryData(key, data);
        });
      }

      onError?.(error, variables, context);
    },
    ...mutationOptions,
  });
};

/**
 * ⚡ PERFORMANCE OPTIMIZATION: Enhanced prefetch hook with warmup capabilities
 */
export const usePrefetchMCP = () => {
  const queryClient = useQueryClient();

  return {
    /**
     * Prefetch a single query
     */
    prefetch: ({ server, tool, args = {}, queryKey, cacheStrategy = 'SEMI_STATIC' }) => {
      const requestKey = JSON.stringify({ server, tool, args });
      const cacheConfig = CACHE_TIMES[cacheStrategy] || CACHE_TIMES.SEMI_STATIC;

      return queryClient.prefetchQuery({
        queryKey,
        queryFn: () => deduplicatedCallMCPTool(server, tool, args, requestKey),
        ...cacheConfig,
      });
    },

    /**
     * Prefetch multiple queries in parallel
     */
    prefetchMany: (queries) => {
      return Promise.all(
        queries.map(({ server, tool, args = {}, queryKey, cacheStrategy = 'SEMI_STATIC' }) => {
          const requestKey = JSON.stringify({ server, tool, args });
          const cacheConfig = CACHE_TIMES[cacheStrategy] || CACHE_TIMES.SEMI_STATIC;

          return queryClient.prefetchQuery({
            queryKey,
            queryFn: () => deduplicatedCallMCPTool(server, tool, args, requestKey),
            ...cacheConfig,
          });
        })
      );
    },

    /**
     * Warm up the cache with frequently accessed data
     */
    warmupDashboard: async () => {
      const warmupQueries = [
        { server: 'CIPP-MCP', tool: 'list_tenants', queryKey: queryKeys.cipp.tenants() },
        { server: 'Sophos-MCP', tool: 'list_tenants', queryKey: queryKeys.sophos.tenants() },
        { server: 'Acronis-MCP', tool: 'list_tenants', queryKey: queryKeys.acronis.tenants() },
        { server: 'Autotask-MCP', tool: 'list_companies', queryKey: queryKeys.autotask.companies() },
      ];

      return Promise.allSettled(
        warmupQueries.map(({ server, tool, queryKey }) => {
          const requestKey = JSON.stringify({ server, tool, args: {} });
          return queryClient.prefetchQuery({
            queryKey,
            queryFn: () => deduplicatedCallMCPTool(server, tool, {}, requestKey),
            ...CACHE_TIMES.SEMI_STATIC,
          });
        })
      );
    },

    /**
     * Invalidate all queries for a specific MCP server
     */
    invalidateServer: (serverKey) => {
      if (queryKeys[serverKey]?.all) {
        return queryClient.invalidateQueries({ queryKey: queryKeys[serverKey].all });
      }
    },

    /**
     * Clear entire cache (use sparingly)
     */
    clearCache: () => queryClient.clear(),
  };
};

// Convenience hooks for specific MCP servers

/**
 * CIPP-MCP Hooks
 */
export const useCIPPTenants = (options = {}) => {
  return useMCPQuery({
    server: 'CIPP-MCP',
    tool: 'list_tenants',
    queryKey: queryKeys.cipp.tenants(),
    ...options,
  });
};

export const useCIPPUsers = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'CIPP-MCP',
    tool: 'list_users',
    args: { tenantId: tenantId },
    queryKey: queryKeys.cipp.users(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

export const useCIPPLicenses = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'CIPP-MCP',
    tool: 'list_licenses',
    args: { tenantId: tenantId },
    queryKey: queryKeys.cipp.licenses(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

/**
 * Hook to get Microsoft Secure Score from CIPP-MCP
 * Uses get_secure_score tool which returns score.percentage directly
 */
export const useCIPPSecureScore = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'CIPP-MCP',
    tool: 'get_secure_score',
    args: { tenantId: tenantId },
    queryKey: queryKeys.cipp.secureScore(tenantId),
    enabled: !!tenantId,
    cacheStrategy: 'SEMI_STATIC', // Security data doesn't change frequently
    ...options,
  });
};

/**
 * Hook to get M365 upsell summary for sales dashboard
 * Returns: licenses (top 3), Intune status, Conditional Access, Autopilot
 */
export const useCIPPUpsellSummary = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'CIPP-MCP',
    tool: 'get_m365_upsell_summary',
    args: { tenantId: tenantId },
    queryKey: queryKeys.cipp.upsellSummary(tenantId),
    enabled: !!tenantId,
    cacheStrategy: 'SEMI_STATIC', // Upsell data doesn't change frequently
    ...options,
  });
};

/**
 * Sophos-MCP Hooks
 */
export const useSophosTenants = (options = {}) => {
  return useMCPQuery({
    server: 'Sophos-MCP',
    tool: 'list_tenants',
    queryKey: queryKeys.sophos.tenants(),
    ...options,
  });
};

export const useSophosEndpoints = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Sophos-MCP',
    tool: 'get_customer_endpoint_inventory',
    args: { customerId: tenantId },
    queryKey: queryKeys.sophos.endpoints(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

export const useSophosHealth = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Sophos-MCP',
    tool: 'get_customer_endpoint_health',
    args: { customerId: tenantId },
    queryKey: queryKeys.sophos.health(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

export const useSophosFirewalls = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Sophos-MCP',
    tool: 'get_customer_firewall_status',
    args: { customerId: tenantId },
    queryKey: queryKeys.sophos.firewalls(tenantId),
    enabled: !!tenantId,
    cacheStrategy: 'SEMI_STATIC',
    ...options,
  });
};

export const useSophosLicenses = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Sophos-MCP',
    tool: 'get_customer_licenses',
    args: { customerId: tenantId },
    queryKey: queryKeys.sophos.licenses(tenantId),
    enabled: !!tenantId,
    cacheStrategy: 'SEMI_STATIC',
    ...options,
  });
};

/**
 * Get MDR (Managed Detection & Response) status for a Sophos customer
 * Returns hasMdr, tier (Complete/Essentials), active status, and subscription details
 */
export const useSophosMdrStatus = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Sophos-MCP',
    tool: 'get_mdr_status',
    args: { customerId: tenantId },
    queryKey: queryKeys.sophos.mdrStatus(tenantId),
    enabled: !!tenantId,
    cacheStrategy: 'SEMI_STATIC',
    ...options,
  });
};

/**
 * Acronis-MCP Hooks
 */
export const useAcronisTenants = (options = {}) => {
  return useMCPQuery({
    server: 'Acronis-MCP',
    tool: 'list_tenants',
    queryKey: queryKeys.acronis.tenants(),
    ...options,
  });
};

export const useAcronisResources = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Acronis-MCP',
    tool: 'get_protected_resources',
    args: { tenantId: tenantId },
    queryKey: queryKeys.acronis.resources(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

export const useAcronisHealth = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Acronis-MCP',
    tool: 'get_backup_status',
    args: { tenantId: tenantId },
    queryKey: queryKeys.acronis.health(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

/**
 * Hook to get Acronis agent inventory (installed agents with device info)
 * Returns agents with hostname, platform (Windows/Linux/macOS), version, status
 * @param {string} tenantId - Acronis tenant ID
 * @param {object} options - Additional React Query options
 */
export const useAcronisAgents = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Acronis-MCP',
    tool: 'get_agent_inventory',
    args: { tenantId: tenantId },
    queryKey: queryKeys.acronis.agents(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

/**
 * Hook to get Acronis protection policies (backup plans)
 * Returns backup plans with type, enabled status, count
 * @param {string} tenantId - Acronis tenant ID
 * @param {object} options - Additional React Query options
 */
export const useAcronisProtectionPolicies = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Acronis-MCP',
    tool: 'get_protection_policies',
    args: { tenantId: tenantId },
    queryKey: queryKeys.acronis.policies(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

/**
 * Hook to get Acronis resource usage (storage data)
 * Returns storage used/quota per tenant from get_resource_usage tool
 * @param {string} tenantId - Acronis tenant ID
 * @param {object} options - Additional React Query options
 */
export const useAcronisResourceUsage = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Acronis-MCP',
    tool: 'get_resource_usage',
    args: { tenantId: tenantId },
    queryKey: queryKeys.acronis.usage(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

/**
 * Hook to get Acronis Microsoft 365 backup status
 * Returns billing-grade protected item counts (seats/SharePoint sites/Teams),
 * cloud-to-cloud storage, last backup time and recent backup activity.
 * Cloud-to-cloud M365 backups create NO resource_statuses rows, so this is
 * the ONLY source of M365 backup visibility for M365-only Acronis customers.
 * @param {string} tenantId - Acronis tenant ID
 * @param {object} options - Additional React Query options
 */
export const useAcronisM365Backup = (tenantId, options = {}) => {
  return useMCPQuery({
    server: 'Acronis-MCP',
    tool: 'get_m365_backup_status',
    args: { tenantId: tenantId },
    queryKey: queryKeys.acronis.m365(tenantId),
    enabled: !!tenantId,
    ...options,
  });
};

/**
 * Datto-MCP Hooks
 */
export const useDattoDevices = (clientName, options = {}) => {
  return useMCPQuery({
    server: 'Datto-MCP',
    tool: 'list_devices',
    args: {
      clientID: clientName, // Fixed: Datto-MCP uses clientID not clientName
      status: 'all',
      maxRecords: 50,
    },
    queryKey: queryKeys.datto.devices(clientName),
    enabled: !!clientName,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useDattoDeviceStatus = (deviceId, options = {}) => {
  return useMCPQuery({
    server: 'Datto-MCP',
    tool: 'get_device_status',
    args: {
      deviceID: deviceId,
      includeAssets: true,
      includeAlerts: true,
    },
    queryKey: queryKeys.datto.deviceStatus(deviceId),
    enabled: !!deviceId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useDattoAlerts = (clientName, options = {}) => {
  return useMCPQuery({
    server: 'Datto-MCP',
    tool: 'get_backup_alerts',
    args: {
      clientID: clientName, // Fixed: Datto-MCP uses clientID not clientName
      severity: 'all',
      activeOnly: true,
      maxRecords: 20,
    },
    queryKey: queryKeys.datto.alerts(clientName),
    enabled: !!clientName,
    staleTime: 1 * 60 * 1000, // 1 minute (alerts are time-sensitive)
    ...options,
  });
};

export const useDattoEndpointAgents = (organizationId, options = {}) => {
  return useMCPQuery({
    server: 'Datto-MCP',
    tool: 'list_endpoint_agents',
    args: {
      organizationId: organizationId, // Filter by organization ID
      agentType: 'cc4pc', // PC endpoint agents
      status: 'all',
      maxRecords: 100,
    },
    queryKey: queryKeys.datto.endpointAgents(organizationId),
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

export const useDattoBackupJobs = (clientName, options = {}) => {
  const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  return useMCPQuery({
    server: 'Datto-MCP',
    tool: 'list_backup_jobs',
    args: {
      clientID: clientName, // Fixed: Datto-MCP uses clientID not clientName
      status: 'all',
      startDate: last7Days,
      maxRecords: 50,
    },
    queryKey: queryKeys.datto.backupJobs(clientName),
    enabled: !!clientName,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Hook to get Datto protected assets (BCDR + SaaS)
 * @param {string} clientName - Datto client name
 * @param {string} assetType - 'all' | 'BCDR' | 'SaaS' (default: 'all')
 * @param {object} options - Additional React Query options
 */
export const useDattoProtectedAssets = (organizationId, assetType = 'all', options = {}) => {
  return useMCPQuery({
    server: 'Datto-MCP',
    tool: 'get_protected_assets',
    args: {
      clientID: organizationId, // clientID is actually organizationId in backend
      assetType: assetType === 'all' ? undefined : assetType,
      protectionStatus: 'all',
      maxRecords: 100,
    },
    queryKey: queryKeys.datto.protectedAssets(organizationId, assetType),
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    ...options,
  });
};

/**
 * Hook to get Datto SaaS Protection domains (M365, Google Workspace)
 * Supports filtering by organizationId OR saasCustomerId
 *
 * Backward compatible with two calling patterns:
 * - Old: useDattoSaasDomains(organizationId, options)
 * - New: useDattoSaasDomains({organizationId, saasCustomerId}, options)
 *
 * @param {string|object} paramsOrOrgId - Filter parameters object OR organizationId string (legacy)
 * @param {object} options - Additional React Query options
 */
export const useDattoSaasDomains = (paramsOrOrgId, options = {}) => {
  // Handle both calling patterns for backward compatibility
  let organizationId = null;
  let saasCustomerId = null;

  if (typeof paramsOrOrgId === 'string') {
    // Legacy pattern: useDattoSaasDomains(organizationId, options)
    organizationId = paramsOrOrgId;
  } else if (typeof paramsOrOrgId === 'object' && paramsOrOrgId !== null) {
    // New pattern: useDattoSaasDomains({organizationId, saasCustomerId}, options)
    organizationId = paramsOrOrgId.organizationId || null;
    saasCustomerId = paramsOrOrgId.saasCustomerId || null;
  }

  // Build args with only non-null values
  const args = {};
  if (organizationId) args.organizationId = organizationId;
  if (saasCustomerId) args.saasCustomerId = saasCustomerId;

  // Generate unique query key
  const queryKey = saasCustomerId
    ? [...queryKeys.datto.saasDomains('saas'), saasCustomerId]
    : queryKeys.datto.saasDomains(organizationId);

  return useMCPQuery({
    server: 'Datto-MCP',
    tool: 'list_saas_domains',
    args,
    queryKey,
    enabled: !!(organizationId || saasCustomerId),  // Enabled if either ID present
    staleTime: 5 * 60 * 1000, // 5 minutes cache (SaaS data changes infrequently)
    ...options,
  });
};

/**
 * Autotask-MCP Hooks
 */
export const useAutotaskCompanies = (options = {}) => {
  return useMCPQuery({
    server: 'Autotask-MCP',
    tool: 'list_companies',
    queryKey: queryKeys.autotask.companies(),
    ...options,
  });
};

export const useAutotaskTickets = (companyId, options = {}) => {
  return useMCPQuery({
    server: 'Autotask-MCP',
    tool: 'get_tickets',
    args: { companyID: companyId, daysBack: 30, maxRecords: 200 },
    queryKey: queryKeys.autotask.tickets(companyId),
    enabled: !!companyId,
    ...options,
  });
};

// Stats are computed from tickets data - useAutotaskTickets provides the data
export const useAutotaskStats = (companyId, options = {}) => {
  return useMCPQuery({
    server: 'Autotask-MCP',
    tool: 'get_tickets',
    args: { companyID: companyId, daysBack: 30, maxRecords: 200 },
    queryKey: queryKeys.autotask.stats(companyId),
    enabled: !!companyId,
    ...options,
  });
};

export const useAutotaskSLA = (companyId, options = {}) => {
  return useMCPQuery({
    server: 'Autotask-MCP',
    tool: 'get_sla_results',
    args: { companyID: companyId },
    queryKey: queryKeys.autotask.sla(companyId),
    enabled: !!companyId,
    ...options,
  });
};

/**
 * ScalePad-MCP Hooks
 */
export const useScalePadOrganizations = (options = {}) => {
  return useMCPQuery({
    server: 'ScalePad-MCP',
    tool: 'list_clients',
    queryKey: queryKeys.scalepad.organizations(),
    ...options,
  });
};

export const useScalePadHardware = (orgId, options = {}) => {
  return useMCPQuery({
    server: 'ScalePad-MCP',
    tool: 'query_hardware_inventory',
    args: { client_id: orgId },
    queryKey: queryKeys.scalepad.hardware(orgId),
    enabled: !!orgId,
    ...options,
  });
};

/**
 * Odin-MCP Hooks
 */
export const useOdinCustomers = (options = {}) => {
  return useMCPQuery({
    server: 'Odin-MCP',
    tool: 'list_customers',
    queryKey: queryKeys.odin.customers(),
    ...options,
  });
};

export const useOdinHealth = (customerId, options = {}) => {
  return useMCPQuery({
    server: 'Odin-MCP',
    tool: 'get_health_score',
    args: { customer_id: customerId },
    queryKey: queryKeys.odin.health(customerId),
    enabled: !!customerId,
    ...options,
  });
};

/**
 * Hook to get customer platform mappings from Odin-MCP
 * Used to resolve CIPP tenant IDs to platform-specific IDs (Sophos, Acronis, etc.)
 *
 * @param {string} customerId - Customer identifier (e.g., 'faucets', 'barristers')
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with platformIds data
 */
export const useCustomerMapping = (customerId, options = {}) => {
  const customersQuery = useOdinCustomers({
    staleTime: 10 * 60 * 1000, // 10 minutes - mappings rarely change
    ...options,
  });

  // Find the customer in the list and extract their platform IDs
  const customer = customersQuery.data?.customers?.find(
    c => c.id?.toLowerCase() === customerId?.toLowerCase()
  );

  return {
    ...customersQuery,
    data: customer || null,
    platformIds: customer?.platformIds || null,
  };
};

/**
 * Hook to resolve CIPP tenant ID to Sophos tenant ID
 * Searches through customer mappings to find a matching CIPP ID
 *
 * @param {string} cippTenantId - CIPP (Azure AD) tenant ID
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with sophosId
 */
export const useSophosIdFromCipp = (cippTenantId, options = {}) => {
  const customersQuery = useOdinCustomers({
    staleTime: 10 * 60 * 1000,
    ...options,
  });

  // Find customer by CIPP tenant ID and return their Sophos ID
  const customer = findCustomerByCipp(customersQuery.data?.customers, cippTenantId);

  return {
    ...customersQuery,
    sophosId: customer?.platformIds?.sophos || null,
    customer: customer || null,
    isResolved: !!customer?.platformIds?.sophos,
  };
};

/**
 * Hook to resolve CIPP tenant ID to Acronis tenant ID
 * Searches through customer mappings to find a matching CIPP ID
 *
 * @param {string} cippTenantId - CIPP (Azure AD) tenant ID
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with acronisId
 */
export const useAcronisIdFromCipp = (cippTenantId, options = {}) => {
  const customersQuery = useOdinCustomers({
    staleTime: 10 * 60 * 1000,
    ...options,
  });

  // Find customer by CIPP tenant ID and return their Acronis ID
  const customer = findCustomerByCipp(customersQuery.data?.customers, cippTenantId);

  return {
    ...customersQuery,
    acronisId: customer?.platformIds?.acronis || null,
    customer: customer || null,
    isResolved: !!customer?.platformIds?.acronis,
  };
};

/**
 * Hook to resolve CIPP tenant ID to Autotask company ID (integer)
 * Searches through customer mappings to find a matching CIPP ID
 *
 * @param {string} cippTenantId - CIPP (Azure AD) tenant ID
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with autotaskId (integer company ID)
 */
export const useAutotaskIdFromCipp = (cippTenantId, options = {}) => {
  const customersQuery = useOdinCustomers({
    staleTime: 10 * 60 * 1000,
    ...options,
  });

  // Find customer by CIPP tenant ID and return their Autotask company ID
  const customer = findCustomerByCipp(customersQuery.data?.customers, cippTenantId);

  // Autotask company IDs are integers, ensure we return the correct type
  const autotaskId = customer?.platformIds?.autotask;

  return {
    ...customersQuery,
    autotaskId: autotaskId !== undefined ? autotaskId : null,
    customer: customer || null,
    isResolved: autotaskId !== undefined && autotaskId !== null,
  };
};

/**
 * Hook to resolve CIPP tenant ID to Datto identifiers
 * Searches through customer mappings to find a matching CIPP ID
 * Handles both legacy string format and new DattoIdentifiers object format
 *
 * @param {string} cippTenantId - CIPP (Azure AD) tenant ID
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with normalized datto object and legacy dattoClient field
 */
export const useDattoIdFromCipp = (cippTenantId, options = {}) => {
  const customersQuery = useOdinCustomers({
    staleTime: 10 * 60 * 1000,
    ...options,
  });

  // Find customer by CIPP tenant ID
  const customer = findCustomerByCipp(customersQuery.data?.customers, cippTenantId);

  const datto = customer?.platformIds?.datto;

  // Normalize datto field (handle string or object format)
  let organizationId = null;
  let saasCustomerId = null;
  let dattoDisplayName = null;

  if (datto) {
    if (typeof datto === 'string') {
      // Legacy format: string is organizationId
      organizationId = datto;
      dattoDisplayName = `Org ${datto}`;
    } else if (typeof datto === 'object') {
      // New format: object with multiple IDs
      organizationId = datto.organizationId || null;
      saasCustomerId = datto.saasCustomerId || null;
      dattoDisplayName = datto.organizationName || datto.saasCustomerName || null;
    }
  }

  return {
    ...customersQuery,
    // Legacy compatibility
    dattoClient: organizationId,
    // New structured data
    datto: {
      organizationId,
      saasCustomerId,
      displayName: dattoDisplayName,
      hasBCDR: !!organizationId,
      hasSaaS: !!saasCustomerId,
    },
    customer: customer || null,
    isResolved: !!(organizationId || saasCustomerId),
  };
};

/**
 * Hook to resolve CIPP tenant ID to ScalePad site/client ID
 * Searches through customer mappings to find a matching CIPP ID
 *
 * @param {string} cippTenantId - CIPP (Azure AD) tenant ID
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with scalepadId (site UUID)
 */
export const useScalePadIdFromCipp = (cippTenantId, options = {}) => {
  const customersQuery = useOdinCustomers({
    staleTime: 10 * 60 * 1000,
    ...options,
  });

  // Find customer by CIPP tenant ID and return their ScalePad site ID
  const customer = findCustomerByCipp(customersQuery.data?.customers, cippTenantId);

  const scalepadId = customer?.platformIds?.scalepad;

  return {
    ...customersQuery,
    scalepadId: scalepadId || null,
    customer: customer || null,
    isResolved: !!scalepadId,
  };
};

/**
 * Hook to get warranty/EOL status from ScalePad
 * @param {string} clientId - ScalePad client/site ID
 * @param {object} options - Additional React Query options
 */
export const useScalePadWarranty = (clientId, options = {}) => {
  return useMCPQuery({
    server: 'ScalePad-MCP',
    tool: 'query_hardware_lifecycle',
    args: { client_id: clientId },
    queryKey: queryKeys.scalepad.warranty(clientId),
    enabled: !!clientId,
    ...options,
  });
};

/**
 * Hook to get client hardware summary from ScalePad
 * @param {string} clientId - ScalePad client/site ID
 * @param {object} options - Additional React Query options
 */
export const useScalePadClientSummary = (clientId, options = {}) => {
  return useMCPQuery({
    server: 'ScalePad-MCP',
    tool: 'get_client_hardware_summary',
    args: { client_id: clientId },
    queryKey: [...queryKeys.scalepad.hardware(clientId), 'summary'],
    enabled: !!clientId,
    ...options,
  });
};

/**
 * Hook to list hardware assets with lifecycle information from ScalePad
 * @param {string} clientId - ScalePad client/site ID
 * @param {object} filterOptions - Optional filters (type, warranty_status, risk_level, etc.)
 * @param {object} options - Additional React Query options
 */
export const useScalePadHardwareAssets = (clientId, filterOptions = {}, options = {}) => {
  return useMCPQuery({
    server: 'ScalePad-MCP',
    tool: 'list_hardware_assets',
    args: { client_id: clientId, ...filterOptions },
    queryKey: [...queryKeys.scalepad.hardware(clientId), 'assets', filterOptions],
    enabled: !!clientId,
    ...options,
  });
};

/**
 * Hook to get platform configuration status for a customer
 * Shows which platforms (Sophos, Acronis, etc.) are configured for a given CIPP tenant
 *
 * @param {string} cippTenantId - CIPP (Azure AD) tenant ID
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with platform status
 */
export const useCustomerPlatformStatus = (cippTenantId, options = {}) => {
  const customersQuery = useOdinCustomers({
    staleTime: 10 * 60 * 1000, // 10 minutes - mappings rarely change
    ...options,
  });

  // Find customer by CIPP tenant ID
  const customer = findCustomerByCipp(customersQuery.data?.customers, cippTenantId);

  const platformIds = customer?.platformIds || {};

  // Determine which platforms are configured
  // Same rule as the picker's (customerKey.isPlatformIdSet): a Datto object with every field
  // null is not configured; Autotask's numeric id 0 is
  const platforms = {
    cipp: isPlatformIdSet(platformIds.cipp),
    sophos: isPlatformIdSet(platformIds.sophos),
    acronis: isPlatformIdSet(platformIds.acronis),
    autotask: isPlatformIdSet(platformIds.autotask),
    scalepad: isPlatformIdSet(platformIds.scalepad),
    datto: isPlatformIdSet(platformIds.datto),
  };

  // Count configured platforms
  const configuredCount = Object.values(platforms).filter(Boolean).length;

  return {
    ...customersQuery,
    platforms,
    platformIds,
    configuredCount,
    totalPlatforms: 6,
    customer: customer || null,
    isMapped: !!customer,
    displayName: customer?.displayName || null,
  };
};

// ==========================================
// Knowledge-MCP Hooks
// ==========================================

/**
 * Hook to list knowledge sources with optional department filter
 * @param {object} filterOptions - Filter options
 * @param {string} [filterOptions.department] - Filter by department (admin, sales, service, security)
 * @param {string} [filterOptions.sourceType] - Filter by type (url, document)
 * @param {number} [filterOptions.limit] - Maximum sources to return
 * @param {number} [filterOptions.offset] - Pagination offset
 * @param {object} options - Additional React Query options
 */
export const useKnowledgeSources = (filterOptions = {}, options = {}) => {
  const args = {};
  if (filterOptions.department) args.department = filterOptions.department;
  if (filterOptions.sourceType) args.source_type = filterOptions.sourceType;
  if (filterOptions.limit) args.limit = filterOptions.limit;
  if (filterOptions.offset) args.offset = filterOptions.offset;

  return useMCPQuery({
    server: 'Knowledge-MCP',
    tool: 'list_sources',
    args,
    queryKey: queryKeys.knowledge.sources(filterOptions.department || 'all'),
    ...CACHE_TIMES.SEMI_STATIC,
    ...options,
  });
};

/**
 * Hook to search the knowledge base using hybrid vector + keyword matching
 * Only fires when query is at least 2 characters
 * @param {string} query - Search query text
 * @param {object} searchOptions - Search options
 * @param {string} [searchOptions.department] - Filter by department
 * @param {string} [searchOptions.sourceId] - Filter by specific source
 * @param {number} [searchOptions.matchCount] - Maximum results (default 10)
 * @param {object} options - Additional React Query options
 */
export const useKnowledgeSearch = (query, searchOptions = {}, options = {}) => {
  const args = { query: query || '' };
  if (searchOptions.department) args.department = searchOptions.department;
  if (searchOptions.sourceId) args.source_id = searchOptions.sourceId;
  if (searchOptions.matchCount) args.match_count = searchOptions.matchCount;

  return useMCPQuery({
    server: 'Knowledge-MCP',
    tool: 'hybrid_search',
    args,
    queryKey: queryKeys.knowledge.search(query, searchOptions.department || 'all'),
    enabled: !!query && query.length >= 2,
    ...CACHE_TIMES.DYNAMIC,
    ...options,
  });
};

/**
 * Hook to get knowledge base statistics
 * @param {string} [department] - Optional department filter
 * @param {object} options - Additional React Query options
 */
export const useKnowledgeStats = (department, options = {}) => {
  const args = {};
  if (department) args.department = department;

  return useMCPQuery({
    server: 'Knowledge-MCP',
    tool: 'get_kb_stats',
    args,
    queryKey: queryKeys.knowledge.stats(department || 'all'),
    ...CACHE_TIMES.SEMI_STATIC,
    ...options,
  });
};

/**
 * Hook to poll crawl job status
 * Automatically refetches every 5 seconds while the job is active
 * @param {string} jobId - Crawl job ID from addUrlSource
 * @param {object} options - Additional React Query options
 */
export const useKnowledgeCrawlStatus = (jobId, options = {}) => {
  return useMCPQuery({
    server: 'Knowledge-MCP',
    tool: 'get_crawl_status',
    args: { job_id: jobId },
    queryKey: queryKeys.knowledge.crawlStatus(jobId),
    enabled: !!jobId,
    refetchInterval: 3000, // Poll every 3 seconds
    ...CACHE_TIMES.REALTIME,
    ...options,
  });
};

/**
 * Mutation hook to sync platform IDs for a single customer.
 * Calls Odin-MCP's sync_single_customer tool to discover platform IDs
 * across Sophos, Acronis, Autotask, and ScalePad.
 *
 * @returns {object} Mutation result with mutate function and status
 */
export const useSyncCustomerPlatforms = () => {
  const queryClient = useQueryClient();

  const mutation = useMCPMutation({
    server: 'Odin-MCP',
    tool: 'sync_single_customer',
    onSuccess: () => {
      // Invalidate customer list to refetch with new mapping
      queryClient.invalidateQueries({ queryKey: queryKeys.odin.customers() });
    },
  });

  // Wrap mutate to always include force: true for re-sync
  return {
    ...mutation,
    mutate: (args, options) => {
      mutation.mutate({ ...args, force: true }, options);
    },
    mutateAsync: async (args, options) => {
      return mutation.mutateAsync({ ...args, force: true }, options);
    },
  };
};

export default {
  queryKeys,
  CACHE_TIMES,
  useGatewayHealth,
  useMCPQuery,
  useMCPQueries,
  useMCPMutation,
  usePrefetchMCP,
};
